import React, { useState, useEffect } from 'react';
import { JubileeAuthProvider } from './auth/JubileeAuthContext';
import JubileeNav from './JubileeNav';
import JubileeDashboard from './JubileeDashboard';
import JubileeVideoGallery from './media/JubileeVideoGallery';
import JubileeAudioGallery from './media/JubileeAudioGallery';
import JubileeImageGallery from './media/JubileeImageGallery';
import JubileeDocumentArchive from './media/JubileeDocumentArchive';
import JubileeEventBrowse from './browsing/JubileeEventBrowse';
import JubileeTimelineBrowse from './browsing/JubileeTimelineBrowse';
import JubileeCollectionsBrowse from './browsing/JubileeCollectionsBrowse';
import JubileeGlobalSearch from './search/JubileeGlobalSearch';
import JubileeAdminManagement from './admin/JubileeAdminManagement';
import JubileeLoginModal from './auth/JubileeLoginModal';
import JubileeUploadModal from './admin/JubileeUploadModal';
import JubileeAudioPlayer from './viewers/JubileeAudioPlayer';
import JubileeSpinner from './components/JubileeSpinner';
import { jubileeStorageService } from './services/jubileeStorageService';
import './JubileeStyles.css';

function JubileeContent() {
  const [activeView, setActiveView] = useState('home');
  const [viewParams, setViewParams] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Audio player state
  const [currentAudioTrack, setCurrentAudioTrack] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Modals state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.title = "MDU Platinum Jubilee (1951 - 2026) - Jesuit Madurai Province Archives";
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeView]);

  const handleNavigate = (view, params = {}) => {
    setActiveView(view);
    setViewParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePlayAudio = (track) => {
    if (currentAudioTrack?.id === track.id) {
      setIsAudioPlaying(!isAudioPlaying);
    } else {
      setCurrentAudioTrack(track);
      setIsAudioPlaying(true);
    }
  };

  const allAudios = jubileeStorageService.getMediaByType('audio');

  const handleNextAudio = () => {
    if (!currentAudioTrack) return;
    const currentIndex = allAudios.findIndex((a) => a.id === currentAudioTrack.id);
    const nextIndex = (currentIndex + 1) % allAudios.length;
    setCurrentAudioTrack(allAudios[nextIndex]);
    setIsAudioPlaying(true);
  };

  const handlePrevAudio = () => {
    if (!currentAudioTrack) return;
    const currentIndex = allAudios.findIndex((a) => a.id === currentAudioTrack.id);
    const prevIndex = (currentIndex - 1 + allAudios.length) % allAudios.length;
    setCurrentAudioTrack(allAudios[prevIndex]);
    setIsAudioPlaying(true);
  };

  if (isInitialLoading) {
    return <JubileeSpinner fullPage={true} message="Opening MDU Platinum Jubilee Archive..." />;
  }

  return (
    <div className="jubilee-root pb-5">
      {/* 1. Jubilee Sub-Navigation Bar */}
      <JubileeNav
        activeView={activeView}
        onNavigate={handleNavigate}
        onOpenLogin={() => setShowLoginModal(true)}
        onOpenUpload={() => setShowUploadModal(true)}
      />

      {/* 2. Main View Routing Container */}
      <main>
        {activeView === 'home' && (
          <JubileeDashboard
            onNavigate={handleNavigate}
            onPlayAudioTrack={handlePlayAudio}
          />
        )}

        {activeView === 'videos' && (
          <JubileeVideoGallery initialFilter={viewParams} />
        )}

        {activeView === 'audio' && (
          <JubileeAudioGallery
            onPlayTrack={handlePlayAudio}
            currentPlayingTrack={currentAudioTrack}
            isPlaying={isAudioPlaying}
            initialFilter={viewParams}
          />
        )}

        {activeView === 'images' && (
          <JubileeImageGallery initialFilter={viewParams} />
        )}

        {activeView === 'documents' && (
          <JubileeDocumentArchive initialFilter={viewParams} />
        )}

        {activeView === 'events' && (
          <JubileeEventBrowse
            onPlayAudioTrack={handlePlayAudio}
          />
        )}

        {activeView === 'timeline' && (
          <JubileeTimelineBrowse
            onPlayAudioTrack={handlePlayAudio}
          />
        )}

        {activeView === 'collections' && (
          <JubileeCollectionsBrowse
            onPlayAudioTrack={handlePlayAudio}
          />
        )}

        {activeView === 'search' && (
          <JubileeGlobalSearch
            onPlayAudioTrack={handlePlayAudio}
            initialQuery={viewParams.query || ''}
          />
        )}

        {activeView === 'admin' && (
          <JubileeAdminManagement
            onPlayAudioTrack={handlePlayAudio}
          />
        )}
      </main>

      {/* 3. Global Sticky Audio Player */}
      {currentAudioTrack && (
        <JubileeAudioPlayer
          currentTrack={currentAudioTrack}
          isPlaying={isAudioPlaying}
          onTogglePlay={() => setIsAudioPlaying(!isAudioPlaying)}
          onNextTrack={handleNextAudio}
          onPrevTrack={handlePrevAudio}
          onClose={() => {
            setIsAudioPlaying(false);
            setCurrentAudioTrack(null);
          }}
        />
      )}

      {/* 4. Login Modal */}
      <JubileeLoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
      />

      {/* 5. Upload Ingestion Modal */}
      <JubileeUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onUploadSuccess={(newItem) => {
          if (newItem.mediaType === 'video') handleNavigate('videos');
          else if (newItem.mediaType === 'audio') handleNavigate('audio');
          else if (newItem.mediaType === 'image') handleNavigate('images');
          else handleNavigate('documents');
        }}
      />
    </div>
  );
}

export default function JubileeMain() {
  return (
    <JubileeAuthProvider>
      <JubileeContent />
    </JubileeAuthProvider>
  );
}
